/**
 * Utility constants and functions for working with promises.
 */

/**
 * A promise that never resolves.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const never = new Promise(() => { });

/**
 * A promise that resolves immediately.
 */
export const immediate = Promise.resolve();

/**
 * Delays the execution of a function by the specified number of milliseconds.
 * @param ms - The number of milliseconds to delay.
 * @returns A promise that resolves after the specified delay.
 */
export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Waits for the next animation frame.
 *
 * @see [MDN Documentation for requestAnimationFrame](https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame)
 * @returns A promise that resolves with the timestamp of the next animation frame.
 */
export const nextAnimationFrame = () =>
  new Promise<DOMHighResTimeStamp>((resolve) =>
    requestAnimationFrame((ts) => {
      resolve(ts);
    })
  );

/**
 * Delays the execution of the current thunk until the next call stack.
 * @returns A promise that resolves during the next.
 */
export const nextCallStack = () => delay(0);

/**
 * Delays the execution of the current thunk until the next 'idle' period
 */
export const nextIdlePeriod = () =>
  new Promise<IdleDeadline>((resolve) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(resolve);
    } else {
      // If requestIdleCallback is not available, we can use a fallback
      // that simulates idle behavior using setTimeout.
      setTimeout(() => {
        const start = Date.now();
        resolve({
          didTimeout: false,
          timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
        });
      }, 1);
    }
  });

